// AIChartRenderer.js — Render chart dari JSON spec di dalam AI chat bubble
// Types: bar | barh | stacked_bar | line | area | pie | donut | radar | composed
import React from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, Label,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList,
  ResponsiveContainer,
} from 'recharts';
import { BarChart2, TrendingUp, PieChart as PieIcon, Activity } from 'lucide-react';

// ── Design tokens (match dashboard palette) ───────────────────────────────────
const PALETTE = [
  '#2563EB','#7C3AED','#0D9488','#D97706','#DC2626',
  '#EA580C','#16A34A','#0EA5E9','#8B5CF6','#F59E0B',
  '#06B6D4','#84CC16','#F43F5E','#A855F7','#14B8A6',
];
const getColor = (i, custom) => (custom && custom[i]) ? custom[i] : PALETTE[i % PALETTE.length];

// ── Number formatter ──────────────────────────────────────────────────────────
function fmtNum(v, unit = '', unitPos = 'suffix', dec = 0) {
  if (v === null || v === undefined) return '-';
  const n = parseFloat(v);
  if (isNaN(n)) return String(v);
  const abs = Math.abs(n);
  let s;
  if (abs >= 1_000_000)  s = (n / 1_000_000).toFixed(1) + 'M';
  else if (abs >= 1_000) s = (n / 1_000).toFixed(1)     + 'K';
  else                   s = n.toFixed(dec);
  if (!unit) return s;
  return unitPos === 'prefix' ? unit + s : s + unit;
}

const shortLabel = (v, maxLen = 10) => {
  const s = String(v || '');
  return s.length > maxLen ? s.slice(0, maxLen - 1) + '…' : s;
};

// ── Custom Tooltip ────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label, unit, unitPos }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(15,23,42,0.96)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 10, padding: '10px 14px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.45)', maxWidth: 260,
    }}>
      {label && <p style={{ color: '#94A3B8', fontSize: 11, marginBottom: 6, fontWeight: 600, margin: '0 0 6px' }}>{label}</p>}
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: i < payload.length-1 ? 4 : 0 }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: p.color || p.fill, flexShrink: 0 }} />
          <span style={{ color: '#CBD5E1', fontSize: 11 }}>{p.name || p.dataKey}:</span>
          <span style={{ color: '#F1F5F9', fontSize: 12, fontWeight: 700, marginLeft: 'auto', paddingLeft: 8 }}>
            {fmtNum(p.value, unit, unitPos)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Chart type icon header ────────────────────────────────────────────────────
function ChartHeader({ title, subtitle, type }) {
  const icons = {
    bar: BarChart2, barh: BarChart2, stacked_bar: BarChart2,
    line: TrendingUp, area: TrendingUp,
    pie: PieIcon, donut: PieIcon,
    radar: Activity, composed: Activity,
  };
  const Icon = icons[type] || BarChart2;
  const typeLabels = {
    bar: 'BAR', barh: 'HORIZONTAL BAR', stacked_bar: 'STACKED BAR',
    line: 'LINE', area: 'AREA',
    pie: 'PIE', donut: 'DONUT',
    radar: 'RADAR', composed: 'COMPOSED',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 8, flexShrink: 0,
          background: 'linear-gradient(135deg,#2563EB,#7C3AED)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon style={{ width: 14, height: 14, color: '#fff' }} />
        </div>
        <div>
          {title    && <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>{title}</div>}
          {subtitle && <div style={{ fontSize: 11, color: '#64748B', marginTop: 1 }}>{subtitle}</div>}
        </div>
      </div>
      <span style={{
        fontSize: 10, fontWeight: 700, color: '#2563EB',
        background: '#EFF6FF', border: '1px solid #BFDBFE',
        borderRadius: 6, padding: '2px 7px', flexShrink: 0, whiteSpace: 'nowrap',
      }}>{typeLabels[type] || type.toUpperCase()}</span>
    </div>
  );
}

// ── Donut center label (rendered as SVG inside Recharts) ─────────────────────
function DonutLabel({ viewBox, total, unit, unitPos }) {
  const { cx, cy } = viewBox;
  const text = fmtNum(total, unit, unitPos);
  return (
    <g>
      <text x={cx} y={cy - 8} textAnchor="middle" dominantBaseline="middle"
        style={{ fontSize: '15px', fontWeight: 800, fill: '#0F172A' }}>{text}</text>
      <text x={cx} y={cy + 10} textAnchor="middle" dominantBaseline="middle"
        style={{ fontSize: '10px', fill: '#94A3B8', fontWeight: 500 }}>Total</text>
    </g>
  );
}

// ── Pie / Donut mini-legend ───────────────────────────────────────────────────
function PieLegend({ items, unit, unitPos }) {
  const total = items.reduce((s, d) => s + (d.value || 0), 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minWidth: 0, paddingLeft: 8 }}>
      {items.map((d, i) => {
        const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : '0.0';
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: d.color, flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: '#0F172A', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.name}>{d.name}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#0F172A', flexShrink: 0, textAlign: 'right', minWidth: 42 }}>{fmtNum(d.value, unit, unitPos)}</span>
            <span style={{ fontSize: 10, color: '#94A3B8', flexShrink: 0, textAlign: 'right', minWidth: 46 }}>({pct}%)</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Shared axis / grid styles ─────────────────────────────────────────────────
const TICK  = { fontSize: 10, fill: '#94A3B8' };
const GRID  = { strokeDasharray: '3 3', stroke: '#E2E8F0', vertical: false };
const MARGINS = { top: 8, right: 16, left: 4, bottom: 4 };

// ── Gradient defs component ───────────────────────────────────────────────────
function GradDefs({ series }) {
  return (
    <defs>
      {series.map(s => (
        <linearGradient key={s.key} id={`aicg_${s.key}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%"  stopColor={s.color} stopOpacity={0.85} />
          <stop offset="95%" stopColor={s.color} stopOpacity={0.08} />
        </linearGradient>
      ))}
    </defs>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AIChartRenderer({ spec }) {
  if (!spec || typeof spec !== 'object' || !Array.isArray(spec.data) || !spec.data.length) {
    return (
      <div style={{ padding: '10px 14px', background: '#FFF7ED', border: '1px solid #FED7AA',
        borderRadius: 10, color: '#92400E', fontSize: 12 }}>
        ⚠️ Data chart kosong atau spec tidak valid.
      </div>
    );
  }

  const {
    type        = 'bar',
    title, subtitle,
    data,
    series: seriesDef,
    xKey        = 'name',
    unit        = '',
    unitPosition = 'suffix',
    decimals    = 0,
    height      = 260,
    colors,
  } = spec;

  const h = Math.max(180, Math.min(420, Number(height) || 260));

  // Auto-derive series from data keys if not provided
  const series = seriesDef || (() => {
    const sample = data[0] || {};
    return Object.keys(sample)
      .filter(k => k !== xKey && typeof sample[k] === 'number')
      .map((k, i) => ({ key: k, label: k, color: getColor(i, colors) }));
  })();

  // Inject colors if not in series
  const coloredSeries = series.map((s, i) => ({ ...s, color: s.color || getColor(i, colors) }));

  const ttProps = { unit, unitPos: unitPosition };
  const tickFmt = v => fmtNum(v, unit, unitPosition, decimals);

  // ─── Wrapper ───────────────────────────────────────────────────────────────
  const Wrap = ({ children, noResponsive }) => (
    <div style={{
      background: '#fff', border: '1px solid #E2E8F0',
      borderRadius: 14, padding: '16px 18px',
      boxShadow: '0 1px 8px rgba(0,0,0,0.07)',
    }}>
      <ChartHeader title={title} subtitle={subtitle} type={type} />
      {noResponsive
        ? children
        : <ResponsiveContainer width="100%" height={h}>{children}</ResponsiveContainer>
      }
    </div>
  );

  // ─── BAR (vertical) ────────────────────────────────────────────────────────
  if (type === 'bar' || type === 'stacked_bar') {
    const isStacked = type === 'stacked_bar';
    return (
      <Wrap>
        <BarChart data={data} margin={MARGINS}>
          <GradDefs series={coloredSeries} />
          <CartesianGrid {...GRID} />
          <XAxis dataKey={xKey} tick={TICK} tickFormatter={v => shortLabel(v)} axisLine={false} tickLine={false} />
          <YAxis tick={TICK} tickFormatter={tickFmt} axisLine={false} tickLine={false} width={52} />
          <Tooltip content={<CustomTooltip {...ttProps} />} cursor={{ fill: 'rgba(37,99,235,0.05)' }} />
          {coloredSeries.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {coloredSeries.map((s, i) => (
            <Bar key={s.key} dataKey={s.key} name={s.label || s.key}
              fill={s.color} radius={isStacked ? [0,0,0,0] : [4,4,0,0]}
              maxBarSize={56} stackId={isStacked ? 'stack' : undefined}>
              {coloredSeries.length === 1 && data.map((_, ci) => (
                <Cell key={ci} fill={getColor(ci, colors)} />
              ))}
            </Bar>
          ))}
        </BarChart>
      </Wrap>
    );
  }

  // ─── HORIZONTAL BAR ────────────────────────────────────────────────────────
  if (type === 'barh') {
    const valKey  = coloredSeries[0]?.key || 'value';
    const sorted  = [...data].sort((a, b) => (Number(b[valKey]) || 0) - (Number(a[valKey]) || 0));
    const barH    = Math.max(220, Math.min(500, sorted.length * 36 + 40));
    return (
      <Wrap>
        <BarChart data={sorted} layout="vertical"
          margin={{ top: 4, right: 56, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
          <XAxis type="number" tick={TICK} tickFormatter={tickFmt} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey={xKey} tick={{ ...TICK, fontSize: 11 }}
            tickFormatter={v => shortLabel(v, 18)} axisLine={false} tickLine={false} width={108} />
          <Tooltip content={<CustomTooltip {...ttProps} />} cursor={{ fill: 'rgba(37,99,235,0.05)' }} />
          {coloredSeries.map((s, i) => (
            <Bar key={s.key} dataKey={s.key} name={s.label || s.key}
              fill={s.color} radius={[0, 4, 4, 0]} maxBarSize={28}>
              <LabelList dataKey={s.key} position="right"
                style={{ fontSize: 10, fontWeight: 700, fill: '#475569' }}
                formatter={v => fmtNum(v, unit, unitPosition, decimals)} />
              {coloredSeries.length === 1 && sorted.map((_, ci) => (
                <Cell key={ci} fill={getColor(ci, colors)} />
              ))}
            </Bar>
          ))}
        </BarChart>
      </Wrap>
    );
  }

  // ─── LINE ──────────────────────────────────────────────────────────────────
  if (type === 'line') {
    return (
      <Wrap>
        <LineChart data={data} margin={MARGINS}>
          <CartesianGrid {...GRID} />
          <XAxis dataKey={xKey} tick={TICK} tickFormatter={v => shortLabel(v)} axisLine={false} tickLine={false} />
          <YAxis tick={TICK} tickFormatter={tickFmt} axisLine={false} tickLine={false} width={52} />
          <Tooltip content={<CustomTooltip {...ttProps} />} />
          {coloredSeries.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {coloredSeries.map(s => (
            <Line key={s.key} type="monotone" dataKey={s.key} name={s.label || s.key}
              stroke={s.color} strokeWidth={2.5}
              dot={{ r: 4, fill: s.color, stroke: '#fff', strokeWidth: 2 }}
              activeDot={{ r: 6, stroke: s.color, strokeWidth: 2 }} />
          ))}
        </LineChart>
      </Wrap>
    );
  }

  // ─── AREA ──────────────────────────────────────────────────────────────────
  if (type === 'area') {
    return (
      <Wrap>
        <AreaChart data={data} margin={MARGINS}>
          <GradDefs series={coloredSeries} />
          <CartesianGrid {...GRID} />
          <XAxis dataKey={xKey} tick={TICK} tickFormatter={v => shortLabel(v)} axisLine={false} tickLine={false} />
          <YAxis tick={TICK} tickFormatter={tickFmt} axisLine={false} tickLine={false} width={52} />
          <Tooltip content={<CustomTooltip {...ttProps} />} />
          {coloredSeries.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {coloredSeries.map(s => (
            <Area key={s.key} type="monotone" dataKey={s.key} name={s.label || s.key}
              stroke={s.color} strokeWidth={2.5}
              fill={`url(#aicg_${s.key})`}
              dot={{ r: 3, fill: s.color, stroke: '#fff', strokeWidth: 1 }} />
          ))}
        </AreaChart>
      </Wrap>
    );
  }

  // ─── PIE / DONUT ───────────────────────────────────────────────────────────
  if (type === 'pie' || type === 'donut') {
    const isDonut = type === 'donut';
    const vKey    = coloredSeries[0]?.key || 'value';
    // Mapping DEFENSIF: apa pun bentuk data dari AI, tentukan label & angka dgn benar.
    //   name  ← urutan: d[xKey] (bila string) → d.name (bila string) → field STRING pertama
    //           → (fallback) 'Item N'. Angka TIDAK boleh jadi name.
    //   value ← urutan: d[vKey] numerik → d.value numerik → field NUMBER pertama → 0.
    const firstOfType = (obj, want) => {
      for (const k of Object.keys(obj)) {
        const v = obj[k];
        if (want === 'string' && typeof v === 'string' && v.trim() && isNaN(Number(v))) return v;
        if (want === 'number') { const n = Number(v); if (Number.isFinite(n) && String(v).trim() !== '') return n; }
      }
      return undefined;
    };
    const isStr = (v) => typeof v === 'string' && v.trim() && isNaN(Number(v));
    const pieData = data.map((d, i) => {
      const nameCand = [d[xKey], d.name, d.label, d.category, d.key].find(isStr)
                       ?? firstOfType(d, 'string');
      const valCand  = [d[vKey], d.value, d.count, d.total].map(Number).find(Number.isFinite);
      const value    = Number.isFinite(valCand) ? valCand : (firstOfType(d, 'number') ?? 0);
      return {
        name:  (nameCand != null && String(nameCand).trim()) ? String(nameCand) : `Item ${i + 1}`,
        value,
        color: getColor(i, colors),
      };
    });
    const total = pieData.reduce((s, d) => s + d.value, 0);

    return (
      <div style={{
        background: '#fff', border: '1px solid #E2E8F0',
        borderRadius: 14, padding: '16px 18px',
        boxShadow: '0 1px 8px rgba(0,0,0,0.07)',
      }}>
        <ChartHeader title={title} subtitle={subtitle} type={type} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Pie chart */}
          <div style={{ flex: '0 0 auto', width: Math.min(h, 220), height: Math.min(h, 220) }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name"
                  cx="50%" cy="50%"
                  innerRadius={isDonut ? '52%' : '0%'}
                  outerRadius="82%"
                  paddingAngle={isDonut ? 3 : 0}
                  startAngle={90} endAngle={-270}>
                  {pieData.map((d, i) => (
                    <Cell key={i} fill={d.color} stroke={isDonut ? '#fff' : 'none'} strokeWidth={isDonut ? 2 : 0} />
                  ))}
                  {isDonut && (
                    <Label
                      content={({ viewBox }) => <DonutLabel viewBox={viewBox} total={total} unit={unit} unitPos={unitPosition} />}
                      position="center"
                    />
                  )}
                </Pie>
                <Tooltip content={<CustomTooltip {...ttProps} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {/* Legend */}
          <PieLegend items={pieData} unit={unit} unitPos={unitPosition} />
        </div>
      </div>
    );
  }

  // ─── RADAR ─────────────────────────────────────────────────────────────────
  if (type === 'radar') {
    return (
      <Wrap>
        <RadarChart data={data} margin={MARGINS} outerRadius="72%">
          <PolarGrid stroke="#E2E8F0" />
          <PolarAngleAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#64748B' }} />
          <PolarRadiusAxis angle={30} tick={{ fontSize: 9, fill: '#94A3B8' }} tickFormatter={tickFmt} />
          <Tooltip content={<CustomTooltip {...ttProps} />} />
          {coloredSeries.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {coloredSeries.map(s => (
            <Radar key={s.key} name={s.label || s.key} dataKey={s.key}
              stroke={s.color} fill={s.color} fillOpacity={0.22} strokeWidth={2} />
          ))}
        </RadarChart>
      </Wrap>
    );
  }

  // ─── COMPOSED (bar + line) ─────────────────────────────────────────────────
  if (type === 'composed') {
    const barSeries  = coloredSeries.slice(0, Math.max(1, coloredSeries.length - 1));
    const lineSeries = coloredSeries.slice(barSeries.length);
    return (
      <Wrap>
        <ComposedChart data={data} margin={MARGINS}>
          <GradDefs series={coloredSeries} />
          <CartesianGrid {...GRID} />
          <XAxis dataKey={xKey} tick={TICK} tickFormatter={v => shortLabel(v)} axisLine={false} tickLine={false} />
          <YAxis tick={TICK} tickFormatter={tickFmt} axisLine={false} tickLine={false} width={52} />
          <Tooltip content={<CustomTooltip {...ttProps} />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {barSeries.map(s => (
            <Bar key={s.key} dataKey={s.key} name={s.label || s.key}
              fill={s.color} radius={[4,4,0,0]} maxBarSize={52} />
          ))}
          {lineSeries.map(s => (
            <Line key={s.key} type="monotone" dataKey={s.key} name={s.label || s.key}
              stroke={s.color} strokeWidth={2.5}
              dot={{ r: 4, fill: s.color, stroke: '#fff', strokeWidth: 2 }} />
          ))}
        </ComposedChart>
      </Wrap>
    );
  }

  // ─── Fallback ──────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '10px 14px', background: '#FFF7ED', border: '1px solid #FED7AA',
      borderRadius: 10, color: '#92400E', fontSize: 12 }}>
      ⚠️ Tipe chart "<strong>{type}</strong>" tidak dikenali. Gunakan: bar, barh, stacked_bar, line, area, pie, donut, radar, composed.
    </div>
  );
}

// ── Parse chart spec dari raw JSON string ─────────────────────────────────────
// ── [B] Normalisasi skema chart dari LLM yang menyimpang ─────────────────────
// Renderer butuh { data:[{...}], xKey, series? }. Sebagian model malah keluarkan
// { series:[{name, data:[{name,value}]}] }. Konversi ke bentuk yang dimengerti.
const _num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const _pickX = (d) => (d && (d.name ?? d.label ?? d.x ?? d.key ?? d.category));
const _pickV = (d) => (d && (d.value ?? d.y ?? d.amount ?? d.total ?? d.count));

export const CHART_TYPES = ['bar','barh','stacked_bar','line','area','pie','donut','radar','composed'];

export function isChartSpec(spec) {
  return !!spec && typeof spec === 'object'
    && CHART_TYPES.includes(String(spec.type || '').toLowerCase())
    && (Array.isArray(spec.data) || Array.isArray(spec.series));
}

function normalizeChartSpec(spec) {
  if (!spec || typeof spec !== 'object') return spec;
  // Sudah benar: ada data[] berisi objek
  if (Array.isArray(spec.data) && spec.data.length) return spec;

  // Format Chart.js native: { data: { labels:[], datasets:[{label, data:[]}] } }
  const cd = (spec.data && !Array.isArray(spec.data)) ? spec.data : null;
  if (cd && Array.isArray(cd.labels) && Array.isArray(cd.datasets) && cd.datasets.length) {
    const labels = cd.labels;
    const ds = cd.datasets;
    const xKey = 'name';
    if (ds.length === 1) {
      const arr = ds[0].data || [];
      const data = labels.map((l, i) => ({ name: l, value: _num(arr[i]) }));
      return { ...spec, xKey, data, series: undefined };
    }
    const names = ds.map((d, i) => d.label || d.name || `series${i + 1}`);
    const data = labels.map((l, i) => {
      const row = { name: l };
      ds.forEach((d, di) => { row[names[di]] = _num((d.data || [])[i]); });
      return row;
    });
    return { ...spec, xKey, data, series: names.map(n => ({ key: n, label: n })) };
  }

  // Skema alternatif: series[].data[] berisi {name,value}
  const sarr = Array.isArray(spec.series) ? spec.series : null;
  if (sarr && sarr.length && Array.isArray(sarr[0]?.data)) {
    const xKey = 'name';
    if (sarr.length === 1) {
      const data = (sarr[0].data || []).map(d => ({ name: _pickX(d), value: _num(_pickV(d)) }));
      return { ...spec, xKey, data, series: undefined };
    }
    const labels = sarr.map((s, i) => s.name || s.label || s.key || `series${i + 1}`);
    const rows = new Map();
    sarr.forEach((s, si) => (s.data || []).forEach(d => {
      const x = _pickX(d);
      if (!rows.has(x)) rows.set(x, { name: x });
      rows.get(x)[labels[si]] = _num(_pickV(d));
    }));
    return { ...spec, xKey, data: [...rows.values()], series: labels.map(l => ({ key: l, label: l })) };
  }
  return spec;
}

export function parseChartSpec(jsonStr) {
  if (!jsonStr || !jsonStr.trim()) return null;
  let parsed = null;
  try {
    parsed = JSON.parse(jsonStr.trim());
  } catch {
    // Pass 2: sanitize common AI artifacts lalu retry
    try {
      const sanitized = jsonStr
        .trim()
        // Fence backtick nyasar di ujung (mis. "...}```" tanpa newline) —
        // lapis pertahanan kedua; kasus utamanya sudah ditangani saat
        // MarkdownRenderer memotong blok kode, tapi ini jaga-jaga bila
        // parseChartSpec suatu saat dipanggil dari jalur lain.
        .replace(/`{3,}[A-Za-z0-9_-]*\s*$/, '')
        .replace(/^\s*`{3,}[A-Za-z0-9_-]*/, '')
        .trim()
        .split('\n')
        .filter(line => !/^\s*\.{3},?\s*$/.test(line))
        .join('\n')
        .replace(/,\s*([\]\}])/g, '$1')
        .replace(/"[^"]*\.{3}[^"]*"/g, '"..."');
      parsed = JSON.parse(sanitized);
    } catch {
      return null;
    }
  }
  return normalizeChartSpec(parsed);
}
